/**
 * Active display timezone (TIMEZONE-DISPLAY).
 *
 * Holds the timezone the current view should render times in — sourced from the
 * tenant's `display_timezone` setting where a tenant is in scope, else undefined
 * (the viewer's local browser zone). Wrap a tenant-scoped subtree in
 * `DisplayTimeZoneProvider` fed by that tenant's setting, then use the
 * `useFormatDateTime` / `useFormatDate` hooks at the call sites so times honour
 * the per-tenant preference automatically. Outside a provider the hooks fall
 * back to viewer-local — still labelled, thanks to the underlying formatter.
 */
import { createContext, useCallback, useContext, type ReactNode } from 'react';

import {
  formatDate,
  formatDateTime,
  formatTime,
  formatTimeOfDay,
} from './datetime';

const DisplayTimeZoneContext = createContext<string | undefined>(undefined);

export function DisplayTimeZoneProvider({
  timeZone,
  children,
}: {
  timeZone?: string | null;
  children: ReactNode;
}) {
  return (
    <DisplayTimeZoneContext.Provider value={timeZone || undefined}>
      {children}
    </DisplayTimeZoneContext.Provider>
  );
}

/** The active display zone, or undefined for the viewer's local zone. */
export function useDisplayTimeZone(): string | undefined {
  return useContext(DisplayTimeZoneContext);
}

export function useFormatDateTime() {
  const tz = useDisplayTimeZone();
  return useCallback(
    (v: Parameters<typeof formatDateTime>[0]) => formatDateTime(v, { timeZone: tz }),
    [tz],
  );
}

export function useFormatDate() {
  const tz = useDisplayTimeZone();
  return useCallback(
    (v: Parameters<typeof formatDate>[0]) => formatDate(v, { timeZone: tz }),
    [tz],
  );
}

/** Seconds-precision wall clock (no zone suffix) — see formatTimeOfDay. The
 *  zone comes from the same tenant/display setting as every other formatter
 *  here; a chart using this must state the zone once itself. */
export function useFormatTimeOfDay() {
  const tz = useDisplayTimeZone();
  return useCallback(
    (v: Parameters<typeof formatTimeOfDay>[0]) => formatTimeOfDay(v, { timeZone: tz }),
    [tz],
  );
}

export function useFormatTime() {
  const tz = useDisplayTimeZone();
  return useCallback(
    (v: Parameters<typeof formatTime>[0]) => formatTime(v, { timeZone: tz }),
    [tz],
  );
}
